import * as React from 'react';

// @ts-expect-error - imported from .js/.jsx file
import {MicrosoftTeamsIcon} from 'shells/icon/svg/microsoft-teams-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {ModalHeader} from 'shells/typography/modal-header';

import type {MeetingUpdateInput} from 'nutshell-graphql-types';

import {authToService} from '../../../setup/integrations/auth-to-service';

import {ConnectButton} from './connect-button';
import {ConnectedWithReconfigureButton} from './connected-with-reconfigure-button';
import {DoneButton} from './done-button';
import {useGetTeamsIntegration} from './graphql/hooks/use-get-teams-integration';
import {useSetMicrosoftTeamsIsEnabled} from './graphql/hooks/use-set-microsoft-teams-is-enabled';

import styles from './conferencing-modal-wrapper.module.css';

type Props = {
    meetingId: string;
    onSave?: (arg1: MeetingUpdateInput) => void;
    closeModal: () => void;
    onFailToConnect: () => void;
    windowFeatures: any;
};

export function MicrosoftTeamsModalContent(props: Props) {
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {microsoftTeamsIntegration, refetch} = useGetTeamsIntegration();
    const isTeamsEnabled = microsoftTeamsIntegration
        ? microsoftTeamsIntegration.isEnabledForScheduler
        : false;
    const setMicrosoftTeamsIsEnabled = useSetMicrosoftTeamsIsEnabled();

    const handleEnableMicrosoftTeams = () => {
        setMicrosoftTeamsIsEnabled({isEnabled: true}).then((mutationResults) => {
            const authUrl =
                mutationResults &&
                mutationResults.data &&
                mutationResults.data.setMicrosoftTeamsIsEnabled
                    ? mutationResults.data.setMicrosoftTeamsIsEnabled.authUrl
                    : null;

            if (authUrl) {
                authToService({
                    authUrl,
                    windowFeatures: props.windowFeatures,
                    authCheckUrl: '/office-365/has-auth?apiVersion=2',
                })
                    .then(({hasAuth}) => {
                        if (hasAuth) {
                            refetch();
                        }
                    })
                    .catch((errors) => setError(errors[0]));
            }
        });
    };

    const handleReconfigureClick = () => {
        setMicrosoftTeamsIsEnabled({isEnabled: false}).then(() => handleEnableMicrosoftTeams());
    };

    const handleSave = () => {
        if (props.onSave) {
            props.onSave({
                id: props.meetingId,
                location: null,
                locationType: isTeamsEnabled ? 'TEAMS' : undefined,
            });
        }
    };

    const handleClose = () => {
        if (!isTeamsEnabled && props.onFailToConnect) {
            props.onFailToConnect();
        } else {
            props.closeModal();
        }
    };

    return (
        <div className={styles['content-container']}>
            <ModalHeader>Connect your Microsoft Teams account</ModalHeader>
            <StackLegacy spacing={16}>
                <div>
                    Log in and authenticate with your Teams account to automatically generate
                    meeting links whenever you select this option as your meeting location.
                </div>
                <div>
                    Virtual meeting links will be created and sent to all participants for each
                    booked meeting.
                </div>
                {isTeamsEnabled ? (
                    <ConnectedWithReconfigureButton
                        integrationName='Microsoft Teams'
                        onClick={handleReconfigureClick}
                    />
                ) : (
                    <ConnectButton
                        icon={MicrosoftTeamsIcon}
                        integrationName='Microsoft Teams'
                        onClick={handleEnableMicrosoftTeams}
                    />
                )}
                {error ? <div className={styles.error}>{error}</div> : undefined}
            </StackLegacy>
            {/* @ts-expect-error */}
            <DoneButton onSave={props.onSave ? handleSave : undefined} closeModal={handleClose} />
        </div>
    );
}
